import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Typography,
  Divider,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  useTheme,
  Paper,
  InputAdornment,
  CircularProgress,
  Chip,
  IconButton
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import SendIcon from '@mui/icons-material/Send';
import { useGlobalConfig } from 'context/GlobalContext';
import { ANALYTICS_EVENTS } from 'framework/analytics/AnalyticsEvents';
import { AssistantApis } from 'apis/assistantAPI';
import { formatChatResponse } from 'framework/utility/ErrorUtils';
import { Bot } from 'lucide-react';
import { SendHorizontal } from 'lucide-react';
import { MessageCircleQuestionMark } from 'lucide-react';


export const PilotAssistantPage = () => {
  const theme = useTheme();
  const { rc } = useGlobalConfig();
  const [messages, setMessages] = useState([
    { sender: 'assistant', text: "Hello! I'm your Project Assistant. How can I help you today with project configurations, data, analytics, or other project-related questions?" }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [contextId, setContextId] = useState<string>('');
  const chatEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Generate unique context ID
  const generateContextId = (): string => {
    return `ctx_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  };

  // Reset context ID when component mounts (screen changes)
  useEffect(() => {
    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.ASSISTANT_OPENED);
    const newContextId = generateContextId();
    setContextId(newContextId);
    // Reset messages to initial state when context changes
    setMessages([
      { sender: 'assistant', text: "Hello! I'm your Project Assistant. How can I help you today with project configurations, data, analytics, or other project-related questions?" }
    ]);
    console.log('New context ID generated:', newContextId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array means this runs only on mount

  // Function to manually reset context (useful for debugging or manual reset)
  const resetContext = () => {
    const newContextId = generateContextId();
    setContextId(newContextId);
    setMessages([
      { sender: 'assistant', text: "Hello! I'm your Project Assistant. How can I help you today with project configurations, data, analytics, or other project-related questions?" }
    ]);
    console.log('Context manually reset with new ID:', newContextId);
  };

  

  // Helper function to render formatted text
  const renderFormattedText = (text: string) => {
    // Split text into lines
    const lines = text.split('\n');
    
    return lines.map((line, lineIndex) => {
      const trimmedLine = line.trim();
      
      // Check if line contains code-like content (backticks)
      if (trimmedLine.includes('`')) {
        // Extract text within backticks
        const parts = trimmedLine.split('`');
        return (
          <Box key={lineIndex}>
            {parts.map((part, partIndex) => {
              if (partIndex % 2 === 1) {
                // This is code (odd indices are within backticks)
                return (
                  <Chip
                    key={partIndex}
                    label={part}
                    size="small"
                    sx={{
                      bgcolor: theme.palette.grey[300],
                      fontFamily: 'monospace',
                      fontSize: '0.875rem',
                      mx: 0.5,
                      '& .MuiChip-label': {
                        px: 1,
                      }
                    }}
                  />
                );
              } else {
                // This is regular text
                return part;
              }
            })}
          </Box>
        );
      }
      
      // Check if line starts with numbered list or bullet points
      if (/^\d+\.\s/.test(trimmedLine) || /^[-*]\s/.test(trimmedLine)) {
        return (
          <Box key={lineIndex} sx={{ ml: 2, mb: 0.5 }}>
            {trimmedLine}
          </Box>
        );
      }
      
      // Check if line contains bold text (**text**)
      if (trimmedLine.includes('**')) {
        const parts = trimmedLine.split('**');
        return (
          <Box key={lineIndex}>
            {parts.map((part, partIndex) => {
              if (partIndex % 2 === 1) {
                // This is bold text (odd indices are within **)
                return (
                  <Typography
                    key={partIndex}
                    component="span"
                    sx={{ fontWeight: 'bold' }}
                  >
                    {part}
                  </Typography>
                );
              } else {
                // This is regular text
                return part;
              }
            })}
          </Box>
        );
      }
      
      // Regular line
      return (
        <Box key={lineIndex} >
          {trimmedLine || '\u00A0'} {/* Use non-breaking space for empty lines */}
        </Box>
      );
    });
  };

  const handleSend = async () => {
    if (!input.trim() || isLoading) return;
    
    const userMessage = input.trim();
    setInput('');
    setIsLoading(true);

    rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.ASSISTANT_QUERY_SUBMITTED, {
      query_length: userMessage.length
    });

    // Add user message to chat
    setMessages(msgs => [...msgs, { sender: 'user', text: userMessage }]);
    
    try {
      console.log('Sending message with context ID:', contextId);
      const response = await AssistantApis.getAnswer(rc, { 
        question: userMessage,
        contextId: contextId
      });
      
      if (response.error) {
        setMessages(msgs => [...msgs, { 
          sender: 'assistant', 
          text: `Sorry, I encountered an error: ${response.error.message || 'Unknown error occurred'}` 
        }]);
      } else if (response.data?.answer) {
        // Format the response to handle escaped characters and JSON formatting
        const formattedAnswer = formatChatResponse(response.data.answer);
        rc.analytics.trackEvent(ANALYTICS_EVENTS.PROJECTS.ASSISTANT_RESPONSE_RECEIVED, {
          response_length: formattedAnswer.length
        });
        setMessages(msgs => [...msgs, {
          sender: 'assistant',
          text: formattedAnswer
        }]);
      } else {
        setMessages(msgs => [...msgs, { 
          sender: 'assistant', 
          text: 'Sorry, I couldn\'t process your request. Please try again.' 
        }]);
      }
    } catch (error) {
      setMessages(msgs => [...msgs, { 
        sender: 'assistant', 
        text: 'Sorry, I encountered an error while processing your request. Please try again.' 
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={{ bgcolor: theme.palette.secondary.main, minHeight: '100vh', py: 4 }}>
      <Box sx={{p:4, pt:0}}>
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <Box sx={{gap: 2, px: 3, py:2, borderBottom: `1px solid`, borderColor: theme.palette.secondary.dark }}>
            <Box style={{display: 'flex', alignItems: 'center', gap: 2}}>
              <MessageCircleQuestionMark size={26} style={{color: theme.palette.primary.main, marginRight:10}} />
              <Typography variant="h4" sx={{ mb: 0.5,textAlign: 'left' }}>Project Assistant</Typography>
            </Box>
            <Typography variant="subtitle1" color="text.secondary" sx={{textAlign: 'left' }}>
              Ask about project configurations, data, analytics, or any other project details.
              </Typography>
          </Box>
          <CardContent sx={{px:0,py:0,pb:0}}>
            <Paper elevation={0} sx={{px: 2, py:2,borderRadius: 0, minHeight: 620, maxHeight: 400, mb: 2, overflowY: 'auto', background:"transparent" , borderBottom: `1px solid ${theme.palette.secondary.dark}`}}>
              <List sx={{ minHeight: 180, maxHeight: 360, p: 0 }}>
                {messages.map((msg, idx) => (
                  <ListItem
                    key={idx}
                    sx={{
                      justifyContent: msg.sender === 'user' ? 'flex-end' : 'flex-start',
                      alignItems: 'flex-start',
                      px: 0,
                    }}
                  >

                    <Box sx={{ 
                     bgcolor:  theme.palette.secondary.dark,
                     display:   msg.sender === 'user'? 'none' : 'flex',
                     alignItems: 'center',
                     justifyContent: 'center',
                     marginRight: '10px',
                       borderRadius: "50%",
                       p:1
                       }}>
                      <Bot size={24} />
                    </Box>
                    <Box
                      sx={{
                        bgcolor: msg.sender === 'user'? theme.palette.primary.main : theme.palette.secondary.dark,
                        color: msg.sender === 'user' ? theme.palette.primary.contrastText : theme.palette.text.primary,
                        borderRadius: 2,
                        px: 2,
                        py: 1,
                        maxWidth: '50%',
                        ml: msg.sender === 'user' ? 'auto' : 0,
                        mr: msg.sender === 'user' ? 0 : 'auto',
                        fontWeight: msg.sender === 'user' ? 600 : 400,
                        fontSize: 16,
                        boxShadow: 0,
                        wordBreak: 'break-word',
                        whiteSpace: 'pre-wrap', // Preserve line breaks and spaces
                      }}
                    >
                     
                      {renderFormattedText(msg.text)}
                    </Box>
                  </ListItem>
                ))}
                <div ref={chatEndRef} />
              </List>
            </Paper>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 ,px: 2, pt:1, pb:0}}>
              <TextField
                fullWidth
                size="medium"
                placeholder="Ask about your project..."
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSend(); }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    backgroundColor: theme.palette.secondary.main,
                    borderRadius: 2,
                    '& .MuiInputBase-input': {
                      color: theme.palette.text.primary,
                    },
                  }
                }}
              />
               <IconButton
                        color="primary"
                        onClick={handleSend}
                        disabled={!input.trim() || isLoading}
                        sx={{ 
                          minWidth: 0, 
                          px: 2,
                          borderRadius: 2, 
                          backgroundColor: theme.palette.primary.dark, 
                          '&:hover': { backgroundColor: theme.palette.primary.dark },
                          '&.Mui-disabled': {
                            opacity: 0.7,
                            backgroundColor: theme.palette.primary.dark,
                          }
                        }}
                      >
                        {isLoading ? <CircularProgress size={24} color="inherit" /> : <SendHorizontal color={theme.palette.primary.contrastText} />}
                      </IconButton>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}; 